import { useDropzone } from "react-dropzone";
import { Upload, FileSpreadsheet, X } from "lucide-react";
import { Button } from "./button";

interface FileUploadDropzoneProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
  accept?: Record<string, string[]>;
  disabled?: boolean;
  description?: string;
}

export function FileUploadDropzone({
  file,
  onFileSelect,
  accept = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel": [".xls"],
  },
  disabled = false,
  description = "Supports .xlsx and .xls files",
}: FileUploadDropzoneProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple: false,
    disabled,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
  });

  if (file) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/50">
        <FileSpreadsheet className="h-8 w-8 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{file.name}</p>
          <p className="text-xs text-muted-foreground">
            {(file.size / 1024).toFixed(2)} KB
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onFileSelect(null)}
          disabled={disabled}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`
        flex flex-col items-center justify-center
        rounded-lg border-2 border-dashed p-8
        cursor-pointer transition-colors
        ${
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50"
        }
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      `}
    >
      <input {...getInputProps()} />
      <Upload className="h-10 w-10 text-muted-foreground mb-4" />
      {isDragActive ? (
        <p className="text-sm text-center">Drop the file here...</p>
      ) : (
        <>
          <p className="text-sm text-center mb-2">
            Drag & drop a file here, or click to select
          </p>
          <p className="text-xs text-muted-foreground text-center">
            {description}
          </p>
        </>
      )}
    </div>
  );
}
